import Component from 'flarum/common/Component';
import ItemList from 'flarum/common/utils/ItemList';
import listItems from 'flarum/common/helpers/listItems';
import classList from 'flarum/common/utils/classList';
import abbreviateNumber from 'flarum/common/utils/abbreviateNumber';
import tagsLabel from 'ext:flarum/tags/common/helpers/tagsLabel';
import SeasonBrowser from './SeasonBrowser';
import EpisodeModal from './EpisodeModal';
import app from 'flarum/forum/app';
import { t, text, img, date, runtime, sourceName, pickFromMedia, titleFor, titleYear, igdbTerms, showScores, showSourceLink, gameBackdrop, backdropColor, backdropTagColor, hexColor } from '../util';
import { loadSeason, primeSeason } from '../api';
import { startDiscussion } from '../utils/startDiscussion';
import Button from 'flarum/common/components/Button';
import isDark from 'flarum/common/utils/isDark';
import LinkModal from './LinkModal';

// The admin can hide every score and rating (on by default).
const ring = (value, label, kind) => value && showScores() ? m("div", {
  className: classList('MarqueeHero-score', kind && `MarqueeHero-score--${kind}`)
}, m("span", {
  className: "MarqueeHero-ring",
  style: {
    '--v': value
  }
}, m("b", null, value)), m("span", {
  className: "MarqueeHero-scoreLabel"
}, label)) : null;

/**
 * A screenshot stands in when IGDB has no wide artwork, unless the admin chose
 * artwork only, or no image at all. Titles stored before 1.16 have no
 * backdropShot: their screenshot backdrop was the first one shown.
 */
const gameBackdropOf = d => {
  const mode = gameBackdrop();
  if (mode === 'none') return null;
  const shot = d.backdropShot ?? (d.screenshots || []).includes(d.backdrop);
  return mode === 'artwork' && shot ? null : img(d.backdrop, 'w1280');
};
const credits = pairs => pairs.filter(([, names]) => names && names.length).map(([label, names]) => [label, [].concat(names).join(', ')]);
const years = d => {
  if (d.endYear && d.endYear !== d.year) return `${d.year}–${d.endYear}`;
  return d.status === 'Returning Series' ? `${d.year}–` : d.year;
};
const timeToBeat = ttb => ttb ? m("div", {
  className: "MarqueeHero-ttb"
}, m("h2", {
  className: "MarqueeHero-sectionTitle"
}, t('hero.time_to_beat')), m("dl", null, ['hastily', 'normally', 'completely'].filter(key => ttb[key]).map(key => m("div", null, m("dt", null, t(`hero.ttb_${key}`)), m("dd", null, t('hero.hours', {
  count: ttb[key]
})))))) : null;
const screenshots = list => list && list.length ? m("div", null, m("h2", {
  className: "MarqueeHero-sectionTitle"
}, t('hero.screenshots')), m("ul", {
  className: "MarqueeHero-shots"
}, list.map(shot => m("li", null, m("a", {
  href: img(shot, 'w1280'),
  target: "_blank",
  rel: "noopener noreferrer"
}, m("img", {
  src: img(shot, 'w300'),
  alt: "",
  loading: "lazy",
  decoding: "async"
})))))) : null;
const clock = seconds => `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
const tracklist = tracks => tracks && tracks.length ? m("div", null, m("h2", {
  className: "MarqueeHero-sectionTitle"
}, t('hero.tracklist')), m("ol", {
  className: "MarqueeTracks"
}, tracks.map(track => m("li", null, m("em", null, track.n), m("span", {
  className: "MarqueeTracks-title"
}, track.title, track.artist ? m("small", null, " \xB7 ", track.artist) : null), track.explicit ? m("abbr", {
  className: "MarqueeTracks-explicit",
  title: text('hero.explicit')
}, "E") : null, m("time", null, clock(track.duration)))))) : null;

/** One episode of a show. */
const episodeSubject = (d, season, episode, ep, seasonData, summary) => ({
  poster: seasonData.poster || summary?.poster || d.poster,
  backdrop: ep.still ? img(ep.still, 'w1280') : img(d.backdrop, 'w1280'),
  meta: [t('meta.season_episode', {
    season,
    episode
  }), date(ep.date), runtime(ep.runtime)],
  score: ring(ep.score, t('hero.episode_score')),
  credits: credits([[t('credits.directed_by'), ep.directors], [t('credits.written_by'), ep.writers], [t('credits.network'), d.networks]]),
  overview: ep.overview,
  people: [t('hero.guest_stars'), ep.guests],
  url: `${seasonData.url}/episode/${ep.n}`
});

/** One season of a show. */
const seasonSubject = (d, season, summary, seasonData) => ({
  poster: seasonData?.poster || summary.poster || d.poster,
  backdrop: img(d.backdrop, 'w1280'),
  meta: [summary.name, summary.year, t('meta.episodes_count', {
    count: summary.episodes
  }), d.genres.join(', ')],
  score: ring(summary.score, t('hero.season_score')),
  credits: credits([[t('credits.creator'), d.crew.creator], [t('credits.network'), d.networks]]),
  overview: seasonData?.overview || d.overview,
  people: [t('hero.cast'), d.cast],
  url: `${d.url}/season/${season}`
});

/** The whole title, one builder per kind; anything else is a movie. */
const titleSubjects = {
  tv: d => ({
    poster: d.poster,
    backdrop: img(d.backdrop, 'w1280'),
    meta: [years(d), d.certification, d.networks.join(', '), t('meta.seasons_count', {
      count: (d.seasons || []).length
    }), d.genres.join(', ')],
    score: [ring(d.score, t('hero.user_score')), d.tagline && m("p", {
      className: "MarqueeHero-tagline"
    }, d.tagline)],
    credits: credits([[t('credits.creator'), d.crew.creator], [t('credits.network'), d.networks], [t('credits.status'), d.status]]),
    overview: d.overview,
    people: [t('hero.cast'), d.cast],
    url: d.url
  }),
  music: d => ({
    poster: d.poster,
    backdrop: img(d.backdrop || d.poster, 'w1280'),
    meta: [date(d.date), t(`meta.record_${d.recordType}`), t('meta.tracks_count', {
      count: d.trackCount
    }), runtime(Math.round(d.duration / 60)), d.genres.join(', ')],
    score: d.fans ? m("p", {
      className: "MarqueeHero-fans"
    }, m("i", {
      className: "fas fa-heart",
      "aria-hidden": "true"
    }), " ", t('hero.fans', {
      count: abbreviateNumber(d.fans)
    })) : null,
    credits: credits([[t('credits.artist'), d.artist], [t('credits.label'), d.label]]),
    overview: null,
    extra: [tracklist(d.tracks)],
    url: d.url,
    linkLabel: t('hero.listen_on', {
      source: 'Deezer'
    })
  }),
  book: d => ({
    poster: d.poster,
    backdrop: img(d.poster, 'w1280'),
    meta: [d.year ? t('meta.first_published', {
      year: d.year
    }) : null, d.pages ? t('meta.pages_count', {
      count: d.pages
    }) : null, d.editions > 1 ? t('meta.editions_count', {
      count: d.editions
    }) : null],
    score: [d.subtitle && m("p", {
      className: "MarqueeHero-tagline"
    }, d.subtitle), d.rating && showScores() ? m("p", {
      className: "MarqueeHero-fans"
    }, m("i", {
      className: "fas fa-star",
      "aria-hidden": "true"
    }), " ", t('hero.book_rating', {
      rating: d.rating,
      count: abbreviateNumber(d.ratingCount)
    })) : null, d.readers ? m("p", {
      className: "MarqueeHero-fans"
    }, m("i", {
      className: "fas fa-bookmark",
      "aria-hidden": "true"
    }), " ", t('hero.want_to_read', {
      count: abbreviateNumber(d.readers)
    })) : null, d.subjects.length ? m("ul", {
      className: "MarqueeHero-chips"
    }, d.subjects.map(subject => m("li", null, subject))) : null],
    credits: credits([[t('credits.author'), d.authors], [t('credits.original_title'), d.originalTitle], [t('credits.publisher'), [d.publisher, d.published].filter(Boolean).join(', ')], [t('credits.isbn'), d.isbn]]),
    overview: d.overview,
    untranslated: d.untranslated,
    people: [t('hero.authors'), d.people.some(p => p.photo) ? d.people : []],
    url: d.url
  }),
  game: d => ({
    poster: d.poster,
    backdrop: gameBackdropOf(d),
    meta: [date(d.date), igdbTerms('mode', d.modes, d.modeSlugs).join(', '), igdbTerms('genre', d.genres, d.genreSlugs).join(', ')],
    score: [ring(d.critic, t('hero.critic_rating'), 'critic'), ring(d.users, t('hero.member_rating'), 'users'),
    // What this version is (Expansion, Remake…) first, as in search results.
    d.type || d.platforms.length ? m("ul", {
      className: "MarqueeHero-chips"
    }, d.type ? m("li", {
      className: "MarqueeHero-type"
    }, t(`picker.type.${d.type}`)) : null, d.platforms.map(p => m("li", null, p))) : null],
    credits: credits([[t('credits.developer'), d.crew.developer], [t('credits.publisher'), d.crew.publisher], [t('credits.series'), d.crew.series]]),
    overview: d.overview,
    untranslated: d.untranslated,
    extra: [timeToBeat(d.timeToBeat), screenshots(d.screenshots)],
    url: d.url
  }),
  movie: d => ({
    poster: d.poster,
    backdrop: img(d.backdrop, 'w1280'),
    meta: [date(d.date), d.certification, runtime(d.runtime), d.genres.join(', ')],
    score: [ring(d.score, t('hero.user_score')), d.tagline && m("p", {
      className: "MarqueeHero-tagline"
    }, d.tagline)],
    credits: credits([[t('credits.director'), d.crew.director], [t('credits.writer'), d.crew.writer], [t('credits.based_on'), d.crew.basedOn]]),
    overview: d.overview,
    people: [t('hero.cast'), d.cast],
    url: d.url
  })
};

/** What the hero is about: the whole title, one season, or one episode. */
function subject(media, d, season, episode, seasonData) {
  const summary = season && (d.seasons || []).find(s => s.number === season);
  const ep = episode && seasonData && seasonData.episodes.find(e => e.n === episode);
  if (ep) return episodeSubject(d, season, episode, ep, seasonData, summary);
  if (summary) return seasonSubject(d, season, summary, seasonData);
  return (titleSubjects[media.kind()] || titleSubjects.movie)(d);
}

/**
 * Replaces the discussion hero when the discussion is linked to a movie, show
 * (or one season or episode of it) or game. Keeps the core title and badge
 * classes so themes still apply.
 */
export default class MediaHero extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    const {
      media,
      season,
      seasonData
    } = this.attrs.link;
    primeSeason(media.id(), seasonData);
    this.seasonData = seasonData;
    if (season && !seasonData) {
      loadSeason(media.id(), season).then(data => {
        this.seasonData = data;
        m.redraw();
      }, () => {});
    }
  }

  /**
   * The buttons under the card. Other extensions add to it: each gets the
   * discussion, the linked media and what this card is about (season, episode).
   */
  actionItems() {
    const {
      media,
      season
    } = this.attrs.link;
    const d = media.details();
    const s = subject(...this.subjectArgs());
    const pick = pickFromMedia(media);
    const ep = this.episode();
    const source = sourceName(media.kind());
    const items = new ItemList();

    // The admin can hide the button; the attribution below always stays (TMDB and IGDB ask for it).
    if (showSourceLink()) {
      items.add('source', m("a", {
        className: "Button Button--primary",
        href: s.url,
        target: "_blank",
        rel: "noopener noreferrer"
      }, s.linkLabel || t('hero.view_on', {
        source
      })), 100);
    }
    if (ep) {
      items.add('episode', m("button", {
        type: "button",
        className: "Button",
        onclick: () => app.modal.show(EpisodeModal, {
          link: pick,
          season: this.seasonData,
          episode: ep
        })
      }, m("i", {
        className: "fas fa-info-circle",
        "aria-hidden": "true"
      }), " ", t('hero.episode_details')), 90);
    } else if (d.trailer) {
      items.add('trailer', m("a", {
        className: "Button",
        href: `https://www.youtube.com/watch?v=${d.trailer}`,
        target: "_blank",
        rel: "noopener noreferrer"
      }, m("i", {
        className: "fas fa-play",
        "aria-hidden": "true"
      }), " ", t('hero.trailer')), 90);
    }
    if (season && app.session.user) {
      items.add('discussShow', m("button", {
        type: "button",
        className: "Button",
        onclick: () => startDiscussion(pick)
      }, m("i", {
        className: "fas fa-tv",
        "aria-hidden": "true"
      }), " ", t('hero.discuss_show')), 80);
    }
    items.add('attribution', m("span", {
      className: "MarqueeHero-attribution"
    }, t(`hero.attribution_${source.toLowerCase().replace(' ', '_')}`), d.wikipedia?.url ? [' · ', t('hero.from_wikipedia', {
      a: m("a", {
        href: d.wikipedia.url,
        target: "_blank",
        rel: "noopener noreferrer"
      }),
      license: m("a", {
        href: "https://creativecommons.org/licenses/by-sa/4.0/",
        target: "_blank",
        rel: "noopener noreferrer license"
      })
    })] : null, d.translation?.by === 'deepl' ? [' · ', t('hero.translated_deepl')] : null), -100);
    return items;
  }

  /**
   * No description in the data language (no Wikipedia article, no DeepL): say
   * so, and keep the source's English behind a toggle, as Curio does.
   */
  untranslated(s) {
    const names = new Intl.DisplayNames([app.translator.getLocale() || 'en'], {
      type: 'language'
    });
    const name = code => {
      try {
        return names.of(code);
      } catch (e) {
        return code;
      }
    };
    const other = name(s.untranslated.have);
    return m("div", {
      className: "MarqueeHero-overview MarqueeHero-overview--missing"
    }, m("p", null, t('hero.no_description', {
      language: name(s.untranslated.want.split('-')[0])
    })), m("button", {
      type: "button",
      className: "Button Button--link",
      "aria-expanded": String(!!this.showOther),
      onclick: () => this.showOther = !this.showOther
    }, t(this.showOther ? 'hero.hide_other' : 'hero.show_other', {
      language: other
    })), this.showOther ? m("p", {
      lang: s.untranslated.have
    }, s.overview) : null);
  }

  /** The discussion's titles (linked one first): picking one shows its card here. */
  series(shown) {
    return m("ul", {
      className: "MarqueeHero-series",
      "aria-label": text('hero.series')
    }, this.attrs.series.map(work => m("li", null, m("button", {
      type: "button",
      className: "MarqueeHero-seriesItem",
      "aria-pressed": String(work === shown),
      onclick: () => work !== shown && this.attrs.onswitch(work)
    }, work.title(), work.year() ? m("small", null, work.year()) : null))));
  }
  episode() {
    const {
      episode
    } = this.attrs.link;
    return episode && this.seasonData?.episodes.find(e => e.n === episode) || null;
  }
  subjectArgs() {
    const {
      media,
      season,
      episode
    } = this.attrs.link;
    return [media, media.details(), season, episode, this.seasonData];
  }
  view() {
    const {
      discussion,
      link
    } = this.attrs;
    const {
      media,
      season,
      episode
    } = link;
    const s = subject(...this.subjectArgs());
    const badges = discussion.badges().toArray();
    const tags = discussion.tags && discussion.tags();
    const pick = pickFromMedia(media);
    const ep = this.episode();
    // The card is titled by the work itself. A discussion renamed to something
    // else ("This indie game is one of my favourites") keeps that as a headline
    // above it. Until an episode's data arrives its title isn't known yet.
    const title = discussion.title();
    // Titles made before the year was switched off still carry it: not a rename.
    const workTitle = episode && !ep ? title : titleFor(pick, season, ep);
    // A related title picked from the row is checked against the linked one:
    // the discussion is named after that, not renamed (Wlork, Discuss d/40077 #85).
    const main = this.attrs.main;
    const renamed = main && main.media !== media ? !main.episode && ![false, true].some(year => titleFor(pickFromMedia(main.media), main.season, null, year) === title) : workTitle !== title && titleFor(pick, season, ep, true) !== title;

    // Each title on the card may have its own background (uploaded, or none).
    const own = (discussion.attribute('marqueeBackdrops') || {})[media.id()];
    const backdrop = own === 'none' ? null : own || s.backdrop;
    // Without an image: the admin's colour, the tag's if chosen (as Flarum's own
    // header), or the forum's banner colour (as the welcome banner).
    const color = backdrop ? null : backdropColor() || backdropTagColor() && (tags || []).map(tag => tag.color()).find(Boolean) || hexColor(getComputedStyle(document.body).getPropertyValue('--hero-bg').trim());
    return m("header", {
      className: classList('Hero DiscussionHero MarqueeHero', `MarqueeHero--${media.kind()}`, {
        'MarqueeHero--episode': ep,
        'MarqueeHero--renamed': renamed,
        'MarqueeHero--backdrop': !!backdrop,
        'MarqueeHero--color': !!color,
        'MarqueeHero--light': !!color && !isDark(color)
      }),
      style: color ? {
        '--marquee-bg': color
      } : undefined
    }, backdrop ? m("div", {
      className: "MarqueeHero-backdrop",
      style: {
        backgroundImage: `url(${backdrop})`
      }
    }) : null, discussion.attribute('canMarqueeLink') ? m(Button, {
      className: "Button Button--icon MarqueeHero-edit",
      icon: "fas fa-pen",
      "aria-label": text('link.edit_card'),
      title: text('link.edit_card'),
      onclick: () => app.modal.show(LinkModal, {
        discussion,
        media
      })
    }) : null, m("div", {
      className: "container MarqueeHero-inner"
    }, s.poster ? m("img", {
      className: "MarqueeHero-poster",
      src: img(s.poster, 'w342'),
      alt: "",
      fetchpriority: "high"
    }) : m("span", {
      className: "MarqueeHero-poster"
    }), m("div", {
      className: "MarqueeHero-body"
    }, m("ul", {
      className: "DiscussionHero-items"
    }, tags && tags.length ? m("li", {
      className: "item-tags"
    }, tagsLabel(tags, {
      link: true
    })) : null, badges.length ? m("li", {
      className: "item-badges"
    }, m("ul", {
      className: "DiscussionHero-badges badges"
    }, listItems(badges))) : null, season ? m("li", {
      className: "MarqueeHero-parent"
    }, m("i", {
      className: "fas fa-tv",
      "aria-hidden": "true"
    }), " ", media.year() && titleYear() ? `${media.title()} (${media.year()})` : media.title()) : null), renamed ? m("h1", {
      className: "MarqueeHero-headline"
    }, title) : null, renamed ? m("h2", {
      className: "DiscussionHero-title"
    }, workTitle) : m("h1", {
      className: "DiscussionHero-title"
    }, workTitle), m("ul", {
      className: "MarqueeHero-meta"
    }, s.meta.filter(Boolean).map(part => m("li", null, part))), this.attrs.series ? this.series(media) : null, m("div", {
      className: "MarqueeHero-scores"
    }, s.score), s.credits.length ? m("dl", {
      className: "MarqueeHero-credits"
    }, s.credits.map(([k, v]) => m("div", null, m("dt", null, k), m("dd", null, v)))) : null, s.overview && s.untranslated ? this.untranslated(s) : s.overview ? m("p", {
      className: "MarqueeHero-overview"
    }, s.overview) : null, s.extra || null, s.people && s.people[1] && s.people[1].length ? m("div", null, m("h2", {
      className: "MarqueeHero-sectionTitle"
    }, s.people[0]), m("ul", {
      className: "MarqueeHero-cast"
    }, s.people[1].map(c => m("li", null, c.photo ? m("img", {
      src: img(c.photo, 'w185'),
      alt: "",
      loading: "lazy",
      decoding: "async"
    }) : m("span", {
      className: "Marquee-noPhoto"
    }), m("b", null, c.name), m("span", null, c.role))))) : null, pick.seasons && pick.seasons.length ? m(SeasonBrowser, {
      link: pick,
      season: season,
      episode: episode
    }) : null, m("div", {
      className: "MarqueeHero-links"
    }, this.actionItems().toArray()))));
  }
}
flarum.reg.add('ernestdefoe-marquee', 'forum/components/MediaHero', MediaHero);